import React, { useCallback, useState } from 'react';
import { RouteComponentProps } from 'react-router';
import Layout from '../../../../components/Layout';
import { MainColumn } from '../../../../components/Layout/ThirdPaneLayout';
import { useQuery } from '@apollo/client';
import useNotify from '../../../../hooks/useNotify';
// import PropTypes from 'prop-types'
import Accordion from '../../../../components/Accordion/Accordion';
import AccordionItem from '../../../../components/Accordion/AccordionItem';
import { PortalWithState } from 'react-portal';
import { Flex, Text } from 'rebass';
import {
  Address as AddressType,
  Credential,
} from '../../../../components/Tester/types';
import { GET_MY_COMPANY_PROFILE } from './CompanyProfile.query';
import SelfEditModal from '../../../../components/User/SelfEditModal';
import PageBar from '../../../../components/PageBar/PageBar';
import Button from '../../../../components/Button/Button';
import {
  EmailInput,
  PhoneInput,
  ProfileType,
} from '../../../../components/Profile/types';
import Perm from '../../../../types/perm';
import Address from '../../../../components/Address/Address';
import CredentialEditModal from '../../../../components/Credential/CredentialEditModal';
import credentialsColumns from './Credentials.columns';
import DataTable from '../../../../components/Table/DataTable';
import StripePaymentPortal from '../../../../components/Stripe/StripePaymentPortal';
import SavedPaymentMethod from '../../../../components/Stripe/SavedPaymentMethod';
import { PaymentMethod } from '../../../../components/Stripe/stripe.types';

type CompanyProfileViewParams = Record<string, never>;

type CompanyProfileViewProps = RouteComponentProps<CompanyProfileViewParams>;

export type MyCompany = {
  id: string;
  type: ProfileType;
  name: string;

  image?: {
    contentType: string;
    size: number;
    thumb: string;
    url: string;
  };

  contactAddressId: string | null;
  contactAddress: AddressType | null;
  phones: PhoneInput[];
  emails: EmailInput[];

  credentials: Credential[];

  perms: Perm[];

  user: {
    profileId: string;
    email: string;
  };
};

export type StripePlatformCustomer = {
  defaultPaymentMethod?: PaymentMethod;
};

const CompanyProfileView: React.FC<CompanyProfileViewProps> = (props) => {
  const {
    loading: itemLoading,
    error: itemError,
    data: itemData,
    refetch,
  } = useQuery<{
    me: MyCompany;
    stripePlatformCustomer?: StripePlatformCustomer;
  }>(GET_MY_COMPANY_PROFILE, {
    // skip: !vendorId,
    // variables: { where: { id: vendorId || null } },
    onError: useCallback((error) => {
      showError(`Error loading company profile`, error);
    }, []),
    fetchPolicy: 'network-only',
  });

  const { showError } = useNotify();

  const company = itemData?.me;
  const stripeCustomer = itemData?.stripePlatformCustomer;
  const companyUser = company?.user;
  const credentials = company?.credentials;

  // State
  const [selectedCredential, setSelectedCredential] = useState<Credential>();

  // Safety Cehck
  // How can we avoid this?
  if (!company || !companyUser) {
    return (
      <Layout>
        <MainColumn px="mainContent" pb="mainContent" height="inherit">
          <PageBar title="Profile" />
          Loading...
        </MainColumn>
      </Layout>
    );
  }

  const hasCredentials = !!credentials?.length;

  return (
    <Layout>
      <MainColumn px="mainContent" pb="mainContent" height="inherit">
        {/* This should get hoisted */}
        <PageBar title="Profile" />
        <Flex sx={{ gap: 6, flexDirection: ['column', 'column', 'row'] }}>
          <Flex
            flexDirection="column"
            sx={{ width: 600, maxWidth: ['100%', '100%', '50%'] }}
          >
            {/* Contact */}
            <Accordion
              title="Contact"
              variant="onMain"
              icon={
                <PortalWithState closeOnEsc>
                  {({ openPortal, closePortal, portal }) => (
                    <>
                      <Button
                        variant="secondary"
                        onClick={openPortal}
                        icon="edit"
                      >
                        Edit
                      </Button>
                      {portal(
                        <SelfEditModal
                          onClose={closePortal}
                          profile={company}
                          hide={['type', 'billingAddressId']}
                        />
                      )}
                    </>
                  )}
                </PortalWithState>
              }
            >
              <AccordionItem label="Name" value={company.name} />
              {company.phones.map((phone, idx) => (
                <AccordionItem
                  key={phone.id}
                  label={
                    phone.subType === 'UNKNOWN'
                      ? `Phone ${idx + 1}`
                      : `Phone (${phone.subType})`
                  }
                  value={phone.value}
                />
              ))}
              {company.emails.map((email, idx) => (
                <AccordionItem
                  key={email.id}
                  label="Email"
                  value={email.value}
                />
              ))}
              {company.contactAddress && (
                <AccordionItem
                  label="Mailing Address"
                  value={<Address address={company.contactAddress} />}
                />
              )}
            </Accordion>
            <Accordion
              title="Business Licenses"
              variant="onMain"
              icon={
                <PortalWithState closeOnEsc>
                  {({ openPortal, closePortal, portal }) => (
                    <>
                      <Button
                        variant="primary"
                        icon="plus"
                        onClick={openPortal}
                      >
                        Business License
                      </Button>
                      {portal(
                        <CredentialEditModal
                          action="create"
                          credential={{
                            profileId: company.id,
                            type: 'BUSINESS_LICENSE',
                          }}
                          onClose={() => {
                            refetch();
                            closePortal();
                          }}
                        />
                      )}
                    </>
                  )}
                </PortalWithState>
              }
            >
              {hasCredentials && (
                <PortalWithState closeOnEsc>
                  {({ openPortal, closePortal, portal }) => (
                    <>
                      <DataTable
                        columns={credentialsColumns}
                        items={credentials}
                        onRowClick={(credential: Credential) => {
                          setSelectedCredential(credential);
                          openPortal();
                        }}
                      />
                      {portal(
                        <>
                          {selectedCredential && (
                            <CredentialEditModal
                              action="edit"
                              credential={{
                                ...selectedCredential,
                                profileId: company.id,
                              }}
                              onClose={() => {
                                refetch();
                                closePortal();
                              }}
                            />
                          )}
                        </>
                      )}
                    </>
                  )}
                </PortalWithState>
              )}
              {!hasCredentials && (
                <Text variant="body2" color="fieldBorderHover">
                  You have not added a business licenses yet.
                </Text>
              )}
            </Accordion>
          </Flex>
          <Flex
            flexDirection="column"
            sx={{ width: 600, maxWidth: ['100%', '100%', '50%'] }}
          >
            <Accordion
              title="Payment Method"
              variant="onMain"
              icon={
                <PortalWithState closeOnEsc>
                  {({ openPortal, closePortal, portal }) => (
                    <>
                      <Button
                        variant="secondary"
                        onClick={openPortal}
                        icon={
                          stripeCustomer?.defaultPaymentMethod ? 'edit' : 'plus'
                        }
                      >
                        {stripeCustomer?.defaultPaymentMethod
                          ? 'Edit '
                          : 'Add '}
                      </Button>
                      {portal(
                        <StripePaymentPortal
                          onCloseComplete={() => {
                            closePortal();
                            refetch();
                          }}
                        />
                      )}
                    </>
                  )}
                </PortalWithState>
              }
            >
              <SavedPaymentMethod
                method={stripeCustomer?.defaultPaymentMethod}
              />
            </Accordion>
          </Flex>
        </Flex>
      </MainColumn>
    </Layout>
  );
};

export default CompanyProfileView;
